import axios from 'axios';
import { httpClient } from './httpClient';

const API_URL = '/api/channels';

export interface Conversation {
  id: string;
  channel_id: string;
  channel_type: string;
  channel_name: string;
  customer_id: number;
  customer_name: string;
  customer_phone?: string;
  customer_email?: string;
  customer_avatar?: string;
  subject?: string;
  status: 'pending' | 'assigned' | 'resolved' | 'closed';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  assigned_to?: number;
  assigned_agent_name?: string;
  message_count: number;
  unread_count: number;
  last_message?: {
    id: string;
    content: string;
    sender_type: string;
    created_at: string;
  };
  tags?: string[];
  ticket_id?: number;
  created_at: string;
  updated_at: string;
}

export interface Message {
  id: string;
  conversation_id: string;
  sender_type: 'customer' | 'agent' | 'bot' | 'system';
  sender_id?: number;
  sender_name?: string;
  content: string;
  content_type: 'text' | 'image' | 'file' | 'audio' | 'video' | 'form';
  channel_metadata?: Record<string, any>;
  direction: 'inbound' | 'outbound';
  status: 'pending' | 'sent' | 'delivered' | 'read' | 'failed';
  is_internal_note: boolean;
  attachments?: any[];
  created_at: string;
  form_submission?: any;
  submitted_at?: string;
  sender?: {
    id: string | number;
    name: string;
    avatar?: string;
    type: string;
  };
}

export interface ConversationFilters {
  status?: string[];
  channel_type?: string[];
  assigned_to?: string;
  is_unassigned?: boolean;
  priority?: string[];
  date_from?: string;
  date_to?: string;
  search?: string;
}

export interface FacebookPage {
  id: string;
  name: string;
  category: string;
  category_list?: Array<{ id: string; name: string }>;
  access_token: string;
  tasks: string[];
  picture?: {
    url?: string;
    is_silhouette?: boolean;
  } | null;
  picture_url?: string | null;
  subscribed?: boolean;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface BotRule {
  id: string;
  name: string;
  description?: string;
  trigger_type: 'keyword' | 'intent' | 'time' | 'condition';
  trigger_config: any;
  action_type: 'reply' | 'assign' | 'tag' | 'escalate';
  action_config: any;
  is_active: boolean;
  priority: number;
}

class OmnichannelService {
  // Conversations
  async getConversations(filters: ConversationFilters = {}, page: number = 1, limit: number = 20) {
    const params = new URLSearchParams();
    if (filters.status) filters.status.forEach((s) => params.append('status', s));
    if (filters.channel_type) filters.channel_type.forEach((c) => params.append('channel_type', c));
    if (filters.assigned_to) params.append('assigned_to', filters.assigned_to);
    if (filters.is_unassigned) params.append('is_unassigned', 'true');
    if (filters.priority) filters.priority.forEach((p) => params.append('priority', p));
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.search) params.append('search', filters.search);
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    const response = await httpClient.get<{
      success: boolean;
      data: Conversation[];
      pagination: { total_pages: number };
    }>(`/api/channels/conversations?${params.toString()}`);
    return response;
  }

  async getConversation(id: string) {
    const response = await httpClient.get<{ data: Conversation }>(
      `/api/channels/conversations/${id}`
    );
    return response.data;
  }

  async getConversationByTicketId(ticketId: number) {
    const response = await httpClient.get<{ data: Conversation }>(
      `/api/channels/conversations/ticket/${ticketId}`
    );
    return response.data;
  }

  async getConversationsByCustomerId(customerId: number) {
    const response = await httpClient.get<{ data: Conversation[] }>(
      `/api/channels/conversations/customer/${customerId}`
    );
    return response.data;
  }

  async getMessages(conversationId: string, limit: number = 50, before?: string) {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    if (before) params.append('before', before);

    const response = await httpClient.get<{ success: boolean; data: Message[] }>(
      `/api/channels/conversations/${conversationId}/messages?${params.toString()}`
    );
    return response.data || [];
  }

  async sendMessage(
    conversationId: string,
    content: string,
    isInternalNote: boolean = false,
    attachments: File[] = []
  ) {
    // If no attachments, send as JSON
    if (attachments.length === 0) {
      const response = await httpClient.post<{ data: Message }>(
        `/api/channels/conversations/${conversationId}/messages`,
        {
          content,
          is_internal_note: isInternalNote
        }
      );
      return response.data;
    }

    // If attachments, send as FormData
    const formData = new FormData();
    formData.append('content', content);
    formData.append('is_internal_note', isInternalNote.toString());

    // Add attachments
    attachments.forEach((file) => {
      formData.append('attachments', file);
    });

    const response = await httpClient.post<{ data: Message }>(
      `/api/channels/conversations/${conversationId}/messages`,
      formData
      // Don't set Content-Type header - browser will set it with boundary automatically
    );
    return response.data;
  }

  async sendFormMessage(
    conversationId: string,
    formTemplateId: number,
    content?: string
  ) {
    const response = await httpClient.post<{ data: Message }>(
      `/api/channels/conversations/${conversationId}/messages`,
      {
        content_type: 'form',
        form_template_id: formTemplateId,
        ...(content ? { content } : {})
      }
    );
    return response.data;
  }

  async assignConversation(conversationId: string, agentId: number) {
    const response = await httpClient.post<{ data: Conversation }>(
      `/api/channels/conversations/${conversationId}/assign`,
      {
        agent_id: agentId
      }
    );
    return response.data;
  }

  async createTicketFromConversation(conversationId: string, ticketData: any) {
    const response = await httpClient.post<{
      success: boolean;
      data: { ticket: any; conversation: Conversation };
    }>(`/api/channels/conversations/${conversationId}/ticket`, ticketData);
    return response.data;
  }

  async updateConversationStatus(conversationId: string, status: string) {
    const response = await httpClient.put<{ data: Conversation }>(
      `/api/channels/conversations/${conversationId}/status`,
      {
        status
      }
    );
    return response.data;
  }

  async updateConversationPriority(conversationId: string, priority: string) {
    const response = await httpClient.put<{ data: Conversation }>(
      `/api/channels/conversations/${conversationId}/priority`,
      {
        priority
      }
    );
    return response.data;
  }

  async addTags(conversationId: string, tagIds: string[]) {
    const response = await httpClient.post<{ data: Conversation }>(
      `/api/channels/conversations/${conversationId}/tags`,
      {
        tag_ids: tagIds
      }
    );
    return response.data;
  }

  async removeTags(conversationId: string, tagIds: string) {
    const response = await httpClient.delete<{ data: Conversation }>(
      `/api/channels/conversations/${conversationId}/tags/${tagIds}`
    );
    return response.data;
  }

  async markConversationAsRead(conversationId: string) {
    const response = await httpClient.post<{ success: boolean }>(
      `/api/channels/conversations/${conversationId}/mark-read`
    );
    return response;
  }

  async sendTypingIndicator(conversationId: string) {
    const response = await httpClient.post<{ success: boolean }>(
      `/api/channels/conversations/${conversationId}/typing`
    );
    return response;
  }

  // Messenger Channel
  async getMessengerConfig() {
    const response = await httpClient.get<{ data: any }>(`/api/channels/messenger/config`);
    return response.data;
  }

  async saveMessengerConfig(config: {
    pageId: string;
    pageAccessToken: string;
    appSecret: string;
    verifyToken: string;
  }) {
    const response = await httpClient.post<{ data: any }>(`/api/channels/messenger/config`, config);
    return response.data;
  }

  async testMessengerConnection() {
    const response = await httpClient.post<{ data: any }>(`/api/channels/messenger/test`);
    return response.data;
  }

  async toggleMessengerChannel(isActive: boolean) {
    const response = await httpClient.put<{ data: any }>(`/api/channels/messenger/toggle`, {
      isActive
    });
    return response.data;
  }

  async deleteMessengerConfig() {
    const response = await httpClient.delete<{ data: any }>(`/api/channels/messenger/config`);
    return response.data;
  }

  async getMessengerStats(dateFrom?: string, dateTo?: string) {
    const params = new URLSearchParams();
    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);

    const response = await httpClient.get<{ data: any }>(
      `/api/channels/messenger/stats?${params.toString()}`
    );
    return response.data;
  }

  async getFacebookPages(page: number = 1, limit: number = 10) {
    const response = await httpClient.get<{
      success: boolean;
      data: FacebookPage[];
      pagination: PaginationMeta;
    }>(`/api/channels/messenger/pages`, {
      params: { page, limit }
    });

    return {
      data: response.data || [],
      pagination: response.pagination
    };
  }

  async requestFacebookLogin() {
    return httpClient.post<{ success: boolean; data: string }>(
      `/api/channels/messenger/request-login`
    );
  }

  async subscribeFacebookPages(pages: { page_id: string; access_token: string }[]) {
    const response = await httpClient.post<{ success: boolean; results?: any[] }>(
      `/api/channels/messenger/pages/subscribe`,
      { pages }
    );
    return response;
  }

  async unsubscribeFacebookPages(page_id: string, access_token: string) {
    const response = await httpClient.post<{ success: boolean; page_id: string }>(
      `/api/channels/messenger/pages/unsubscribe`,
      { page_id, access_token }
    );
    return response;
  }

  async getDashboardStats() {
    const response = await httpClient.get<{ data: any }>(`${API_URL}/dashboard/stats`);
    return response.data;
  }

  async getAgentPerformance(days: number = 7) {
    const response = await httpClient.get<{ data: any }>(
      `${API_URL}/dashboard/agent-performance?days=${days}`
    );
    return response.data;
  }

  async getAvailableAgents(channelId: string) {
    const response = await httpClient.get<{ data: any }>(
      `${API_URL}/channels/${channelId}/available-agents`
    );
    return response.data;
  }

  async autoAssignConversation(conversationId: string) {
    const response = await httpClient.post<{ data: any }>(
      `${API_URL}/conversations/${conversationId}/auto-assign`
    );
    return response.data;
  }

  async assignToMe(conversationId: string) {
    const response = await httpClient.post<{ data: any }>(
      `${API_URL}/conversations/${conversationId}/assign-to-me`
    );
    return response.data;
  }

  // Tags
  async getAllTags() {
    const response = await httpClient.get<{ data: any[] }>(`${API_URL}/tags`);
    return response.data;
  }

  async createTag(name: string, color: string, description?: string) {
    const response = await httpClient.post<{ data: any }>(`${API_URL}/tags`, {
      name,
      color,
      description
    });
    return response.data;
  }

  async updateTag(tagId: string, updates: { name?: string; color?: string; description?: string }) {
    const response = await httpClient.put<{ data: any }>(`${API_URL}/tags/${tagId}`, updates);
    return response.data;
  }

  async deleteTag(tagId: string) {
    const response = await httpClient.delete<{ data: any }>(`${API_URL}/tags/${tagId}`);
    return response.data;
  }

  async getConversationTags(conversationId: string) {
    const response = await httpClient.get<{ data: any[] }>(
      `${API_URL}/conversations/${conversationId}/tags`
    );
    return response.data;
  }

  async getConversationJourney(conversationId: string) {
    const response = await httpClient.get<{ data: any[] }>(
      `${API_URL}/conversations/${conversationId}/journey`
    );
    return response.data;
  }

  // Channel Group Access Management
  async getChannelGroups(channelId: string) {
    const response = await httpClient.get<{ success: boolean; groups: any[] }>(
      `${API_URL}/channels/${channelId}/groups`
    );
    return response.groups;
  }

  async updateChannelGroupAccess(channelId: string, groupIds: number[]) {
    const response = await httpClient.put<{ success: boolean; message: string }>(
      `${API_URL}/channels/${channelId}/groups`,
      {
        groupIds
      }
    );
    return response;
  }
}

export default new OmnichannelService();
